// Testing page card modal (2026-10-08, T-194): the exact formula, the lookback control and the live-book baseline statistics for one card, so the logic
// can be read before a run is started. Statistics are the book's closed trades (analyzable share shown), NOT the card's own experiment result.
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { X, RefreshCw, Loader2 } from 'lucide-react'
import { testingCard } from '../lib/api'

const MONO = { fontFamily: 'JetBrains Mono, monospace' }

function Stat({ label, value, note }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3">
      <p className="text-slate-500 text-[10px] tracking-widest">{label}</p>
      <p className="text-slate-200 text-lg font-semibold" style={MONO}>{value}</p>
      {note && <p className="text-slate-600 text-[10px] mt-0.5">{note}</p>}
    </div>
  )
}

const pct = (v, d = 1) => (v === null || v === undefined ? 'n/a' : `${Number(v).toFixed(d)} %`)

export default function StrategyCardModal({ card, onClose, onOpenPanel }) {
  const [info, setInfo] = useState(null)
  const [days, setDays] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const closeRef = useRef(null)

  const load = useCallback(async (d) => {
    setLoading(true)
    setError(null)
    try {
      const r = await testingCard.get(card.id, d)
      if (!r.success) throw new Error(r.error || 'failed')
      setInfo(r)
      setDays(prev => (prev === null ? r.lookback.days : prev))
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }, [card.id])

  useEffect(() => { load(undefined) }, [load])
  useEffect(() => {
    closeRef.current?.focus()
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const st = info?.stats
  return (
    <div role="dialog" aria-modal="true" aria-labelledby="card-modal-title"
         onClick={onClose}
         style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(2,6,23,0.75)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '56px 12px 12px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()}
           className="w-full max-w-2xl rounded-2xl border border-slate-700 bg-slate-950 p-5 space-y-4"
           style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 id="card-modal-title" className="text-slate-100 font-semibold text-base" style={{ fontFamily: 'Rajdhani, sans-serif' }}>{card.name}</h3>
            <p className="text-slate-500 text-xs">{card.tagline}</p>
          </div>
          <button ref={closeRef} onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-200 p-1"><X className="h-4 w-4" /></button>
        </div>

        {loading && !info && <p className="text-slate-500 text-xs flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" />loading</p>}
        {error && <p className="text-rose-400 text-xs break-words">Could not load: {error}</p>}

        {info && (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-slate-400 text-xs flex items-center gap-2">
                Lookback days
                <input type="number" min={info.lookback.min} max={info.lookback.max} value={days ?? info.lookback.default}
                       onChange={e => setDays(Number(e.target.value))}
                       className="w-16 bg-slate-900 border border-slate-800 rounded px-2 py-1 text-slate-300 text-xs" style={MONO} />
              </label>
              <button onClick={() => load(days)} disabled={loading}
                      className="text-xs text-cyan-300 hover:text-cyan-200 flex items-center gap-1 disabled:opacity-50">
                <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />Apply
              </button>
              <span className="text-slate-600 text-[10px]">{info.lookback.min}-{info.lookback.max} days</span>
            </div>

            <div>
              <p className="text-slate-500 text-[10px] tracking-widest mb-1.5">STATISTICS</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat label="WIN RATE" value={pct(st.win_rate_pct)} note={`n = ${st.n_analyzable} analyzable`} />
                <Stat label="MAX DRAWDOWN" value={st.max_drawdown_pct === null ? 'n/a' : `${st.max_drawdown_pct.toFixed(2)} pts`} note="cumulative pnl_pct, peak to trough" />
                <Stat label="DATA ERRORS" value={st.data_errors} note="ghost, unknown or P&L-less rows" />
                <Stat label="EXCLUDED" value={pct(st.excluded_share === null ? null : st.excluded_share * 100)} note={`of ${st.n_total} closed trades`} />
              </div>
              <p className="text-slate-600 text-[10px] mt-1.5">{info.stats_scope}</p>
            </div>

            <div>
              <p className="text-slate-500 text-[10px] tracking-widest mb-1.5">FORMULA EXECUTED</p>
              <pre className="rounded-lg border border-slate-800 bg-black/40 p-3 text-[11px] leading-relaxed text-emerald-200 overflow-x-auto whitespace-pre" style={MONO}>
                {info.formula.join('\n')}
              </pre>
              <p className="text-slate-600 text-[10px] mt-1">Source: <span style={MONO}>{info.source}</span></p>
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 border-t border-slate-800 pt-3">
          <button onClick={onClose} className="text-xs text-slate-400 hover:text-slate-200 px-3 py-1.5">Close</button>
          <button onClick={() => { onOpenPanel(card.id); onClose() }}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-cyan-500/90 text-slate-950 hover:bg-cyan-400">
            Open {card.runnable ? 'controls and results' : 'details'}
          </button>
        </div>
      </div>
    </div>
  )
}
